import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, TriangleAlert, BellRing, Check } from "lucide-react";

const PRIO = {
  alta: "bg-destructive/12 text-destructive border-destructive/25",
  media: "bg-warning/15 text-warning border-warning/30",
  baixa: "bg-muted text-muted-foreground border-border",
};

export default function AlertsTab({ patientId }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = () => api.get(`/patients/${patientId}/alerts`).then((r) => setAlerts(r.data));
  useEffect(() => { load(); }, [patientId]);

  const generate = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/patients/${patientId}/alerts/generate`);
      setAlerts(data);
      toast.success(data.length ? `${data.length} alerta(s) encontrado(s)` : "Nenhum alerta no padrão atual");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const ack = async (aid) => {
    await api.put(`/patients/${patientId}/alerts/${aid}/ack`);
    setAlerts((prev) => prev.map((a) => (a.id === aid ? { ...a, acknowledged: true } : a)));
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-heading text-xl font-medium">Alertas inteligentes</h3>
          <p className="text-sm text-muted-foreground">Baseados no padrão pessoal do paciente, não em médias genéricas.</p>
        </div>
        <Button onClick={generate} disabled={loading} data-testid="generate-alerts-btn">
          {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <BellRing className="w-4 h-4 mr-2" />} Avaliar tendências
        </Button>
      </div>

      {alerts.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed" data-testid="alerts-empty">
          <TriangleAlert className="w-8 h-8 mx-auto mb-3 opacity-40" />
          Nenhum alerta gerado. Adicione dados de wearables/exames e clique em "Avaliar tendências".
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map((a) => (
            <Card key={a.id} className={`p-5 border-border shadow-none ${a.acknowledged ? "opacity-60" : ""}`} data-testid={`alert-${a.id}`}>
              <div className="flex items-start justify-between gap-4">
                <div className="flex gap-3">
                  <TriangleAlert className={`w-5 h-5 mt-0.5 ${a.prioridade === "alta" ? "text-destructive" : "text-warning"}`} />
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{a.titulo}</h4>
                      <Badge variant="outline" className={PRIO[a.prioridade] || PRIO.baixa}>{a.prioridade}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">{a.motivo}</p>
                    <p className="text-xs text-muted-foreground mt-1">Fonte: {a.dados}</p>
                  </div>
                </div>
                {!a.acknowledged && (
                  <Button size="sm" variant="outline" onClick={() => ack(a.id)} data-testid={`ack-alert-${a.id}`}>
                    <Check className="w-4 h-4 mr-1" /> Revisar
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
